import { Box, Stack, Tabs } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LinkTab, getTabProps } from 'src/components/LinkTab';
import { updateUserValidationSchema, ValidationErrors } from 'src/constants/validations';
import { AlertsView } from 'src/modules/alerts/components';
import { CanvaPortalLinks } from 'src/modules/canva/components';
import { selectProfile } from 'src/modules/profile/slices';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { generateUrlWithCompany } from 'src/helpers/helpers';
import { useUpdatePasswordApi, useUpdateUserApi } from '../../api';
import { useGetProfileService } from '../../services';
import { Button, PasswordInput, TextInput } from 'src/UIKit';
import * as Yup from 'yup';

type TabPanelProps = {
  children?: React.ReactNode;
  name: string;
  value: string;
};

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, name, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== name}
      id={`simple-tabpanel-${name}`}
      aria-labelledby={`simple-tab-${name}`}
      {...other}
    >
      {value === name && <>{children}</>}
    </div>
  );
};

export const ProfileView = () => {
  const { tab: urlTab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const { profile } = useSelector(selectProfile);
  const [searchParams] = useSearchParams();
  const urlCompanyId = searchParams.get('companyId');
  const currentCompanyId =
    urlCompanyId || localStorage.getItem('bhiCurrentCompany') || useSelector(getCurrentCompanyId);
  const getProfile = useGetProfileService();
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [formValues, setFormValues] = useState({
    firstName: profile?.firstName || '',
    lastName: profile?.lastName || '',
    newPassword: '',
    currentPassword: '',
    confirmPassword: '',
    phoneNumber: '',
  });
  const updateUserApi = useUpdateUserApi();
  const updatePasswordApi = useUpdatePasswordApi();

  const [isProfileFormDirty, setIsProfileFormDirty] = useState(false);

  const availableTabs = ['profile', 'password', 'canva', 'alerts'];

  const getInitialTab = () => {
    if (urlTab && availableTabs.includes(urlTab)) {
      return urlTab;
    }
    return 'profile';
  };

  const [tab, setTab] = useState(getInitialTab());

  useEffect(() => {
    if (urlTab && availableTabs.includes(urlTab) && urlTab !== tab) {
      setTab(urlTab);
    } else if (!urlTab && tab !== 'profile') {
      setTab('profile');
    }
  }, [urlTab, availableTabs, tab]);

  const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues(prevState => ({
      ...prevState,
      [name]: value,
    }));
    setIsProfileFormDirty(true);
  };

  const validate = async (values: typeof formValues) => {
    try {
      await updateUserValidationSchema.validate(values, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = err.inner?.reduce((acc, curr) => {
          if (curr.path) {
            acc[curr.path] = curr?.message;
          }
          return acc;
        }, {} as ValidationErrors);
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newTab: string) => {
    setTab(newTab);
    navigate(`/profile/${newTab}`);
  };

  const handlePasswordEditClick = async () => {
    if (formValues.currentPassword) {
      if (formValues.newPassword && formValues.newPassword.length < 8) {
        setErrors({
          ...errors,
          newPassword: 'Password must be at least 8 characters long.',
        });
      } else if (!formValues.newPassword) {
        setErrors({ ...errors, newPassword: 'Password is required.' });
      } else if (formValues.newPassword !== formValues.confirmPassword) {
        setErrors({
          ...errors,
          confirmPassword: 'New password and confirm password are not match',
        });
      } else {
        profile?.id &&
          updatePasswordApi(profile.id, {
            currentPassword: formValues.currentPassword,
            password: formValues.newPassword,
          })
            .then(() => {
              setFormValues({
                ...formValues,
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
              });
            })
            .catch(e => {
              if (e.error === 'validation') {
                setErrors({ currentPassword: 'Invalid password' });
              }
            });
      }
    }
  };

  const handleEditClick = async () => {
    const isValid = await validate({
      ...formValues,
      phoneNumber: formValues.phoneNumber.replace('+1', ''),
    });
    if (isValid) {
      const { phoneNumber, firstName, lastName } = formValues;
      const updatedPhoneNumber = phoneNumber ? '+1' + phoneNumber : '';

      profile?.id &&
        updateUserApi(profile.id, {
          phoneNumber: updatedPhoneNumber,
          firstName,
          lastName,
        }).then(async () => {
          setIsProfileFormDirty(false);

          if (currentCompanyId) {
            await getProfile(currentCompanyId);
          }
        });
    }
  };

  const isPasswordButtonDisabled = (): boolean => {
    const { currentPassword, newPassword, confirmPassword } = formValues;
    return !(currentPassword.length > 0 && newPassword.length > 0 && confirmPassword.length > 0);
  };

  useEffect(() => {
    setFormValues({
      ...formValues,
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      phoneNumber: profile?.phoneNumber ? profile?.phoneNumber.replace('+1', '') : '',
    });
  }, [profile]);

  return (
    <Box width="100%">
      <Tabs value={tab} onChange={handleTabChange} aria-label="personal information tab">
        <LinkTab
          label="My Profile"
          value="profile"
          {...getTabProps('profile')}
          to={generateUrlWithCompany('/profile', currentCompanyId)}
        />
        <LinkTab
          label="Password"
          value="password"
          {...getTabProps('password')}
          to={generateUrlWithCompany('/profile/password', currentCompanyId)}
        />
        <LinkTab
          label="Canva"
          value="canva"
          {...getTabProps('canva')}
          to={generateUrlWithCompany('/profile/canva', currentCompanyId)}
        />
        <LinkTab
          label="Alerts"
          value="alerts"
          {...getTabProps('alerts')}
          to={generateUrlWithCompany('/profile/alerts', currentCompanyId)}
        />
      </Tabs>
      <CustomTabPanel value={tab} name="profile">
        <Stack width={300} mt={5} gap={2}>
          <TextInput
            label="First Name"
            name="firstName"
            value={formValues.firstName}
            onChange={handleInputChange}
            autoComplete="false"
            error={!!errors?.firstName}
            helperText={errors?.firstName || ''}
            margin="none"
          />
          <TextInput
            label="Last Name"
            name="lastName"
            type="name"
            value={formValues.lastName}
            onChange={handleInputChange}
            error={!!errors?.lastName}
            helperText={errors?.lastName || ''}
            margin="none"
          />
          <TextInput
            label="Mobile Number"
            name="phoneNumber"
            type="tel"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            InputProps={{
              startAdornment: <InputAdornment position="start">+1</InputAdornment>,
            }}
            value={formValues.phoneNumber}
            onChange={handleInputChange}
            error={!!errors?.phoneNumber}
            helperText={errors?.phoneNumber || ''}
            margin="none"
          />
          <Box mt={2}>
            <Button
              onClick={handleEditClick}
              variant="contained"
              disabled={!isProfileFormDirty}
              color="primary"
            >
              Save
            </Button>
          </Box>
        </Stack>
      </CustomTabPanel>
      <CustomTabPanel value={tab} name="password">
        <Stack width={300} mt={5} gap={2}>
          <PasswordInput
            label="Current Password"
            name="currentPassword"
            value={formValues.currentPassword}
            onChange={handlePasswordInputChange}
            error={!!errors?.currentPassword}
            helperText={errors?.currentPassword || ''}
            margin="none"
          />
          <PasswordInput
            label="New Password"
            name="newPassword"
            value={formValues.newPassword}
            onChange={handlePasswordInputChange}
            error={!!errors?.newPassword}
            helperText={errors?.newPassword || ''}
            margin="none"
          />
          <PasswordInput
            label="Confirm Password"
            name="confirmPassword"
            value={formValues.confirmPassword}
            onChange={handlePasswordInputChange}
            error={!!errors?.confirmPassword}
            helperText={errors?.confirmPassword || ''}
            margin="none"
          />
          <Box mt={2}>
            <Button
              onClick={handlePasswordEditClick}
              disabled={isPasswordButtonDisabled()}
              variant="contained"
              color="primary"
            >
              Change Password
            </Button>
          </Box>
        </Stack>
      </CustomTabPanel>
      <CustomTabPanel value={tab} name="canva">
        <Box width={200} mt={2.5}>
          <CanvaPortalLinks />
        </Box>
      </CustomTabPanel>
      <CustomTabPanel value={tab} name="alerts">
        <Box width="100%" mt={2.5}>
          <AlertsView />
        </Box>
      </CustomTabPanel>
    </Box>
  );
};
